/**
 * The feedback picker steps aside for a dialog that opens AFTER it (#16841).
 *
 * Since #16841 the picker's capture layer is `z-feedback-picker`, above
 * every ordinary modal's `z-modal` (#16990 names; they were 540 and 500), so
 * that a viewer can press `Shift+F` from inside a dialog
 * and pick something in it. The same order has a cost in the other
 * direction: a dialog that opens while the picker is already up (a toast's
 * "Details" action, a keyboard shortcut, a `FeedbackModal` opened from
 * outside the mode) would land UNDER the capture layer, where every click on
 * it is read as a pick and it can never be used.
 *
 * So the picker watches the shared `useModalEscape` stack. The depth when
 * picking starts is the baseline — dialogs already open are the ones the
 * viewer means to pick inside — and any push above that baseline calls
 * `onModalOpened`, which the picker wires to `beginBrowsing()`: the capture
 * layer comes down, the new dialog is on top and usable, and the banner's
 * "Resume selecting" re-arms the picker over it. A pop lowers the baseline,
 * so a dialog that closes and reopens mid-pick is still noticed.
 *
 * Coverage is the stack: `Modal` and `Drawer` both push onto it. A surface
 * that bypasses `useModalEscape` is not seen here.
 *
 * `isPicking` is mirrored into a ref from a LAYOUT effect so the listener —
 * called synchronously from a modal's passive mount effect — reads the
 * committed value. That matters on the anchored path: picking an element
 * sets the anchor, which drops `isPicking` in the same commit that mounts
 * `FeedbackModal`. That push must NOT suspend the mode, or cancelling the
 * form would land in `browsing` instead of back on the picker.
 */

import { useEffect, useLayoutEffect, useRef } from 'react'
import {
  getModalEscapeStackDepth,
  subscribeModalEscapeStack,
} from '@/hooks/use-modal-escape'

interface PickerYieldsToNewModalOptions {
  /** Is the picker's capture layer up (the picker's own `visible`)? */
  isPicking: boolean
  /** A dialog opened above the baseline while picking. */
  onModalOpened: () => void
}

export function usePickerYieldsToNewModal({
  isPicking,
  onModalOpened,
}: PickerYieldsToNewModalOptions): void {
  const isPickingRef = useRef(isPicking)
  const onModalOpenedRef = useRef(onModalOpened)

  useLayoutEffect(() => {
    isPickingRef.current = isPicking
  }, [isPicking])

  useLayoutEffect(() => {
    onModalOpenedRef.current = onModalOpened
  }, [onModalOpened])

  useEffect(() => {
    if (!isPicking) return
    let baseline = getModalEscapeStackDepth()
    return subscribeModalEscapeStack(depth => {
      if (depth <= baseline) {
        baseline = depth
        return
      }
      if (!isPickingRef.current) return
      onModalOpenedRef.current()
    })
  }, [isPicking])
}
